/**
 * TuiAdapter — the per-TUI seam inside the PTY harness. PtySession/PtyHarness
 * own the generic machinery (node-pty spawn, output coalescing, relay bridge,
 * park/reuse, teardown); everything specific to ONE interactive CLI (binary,
 * argv, credential materialization, prompt encoding, exit diagnostics) lives
 * behind this interface. Claude's structured-event machinery (transcript
 * tailer + hook socket + tool servers) stays in PtySession, gated on
 * `capabilities.structuredEvents` — promoting it into the adapter waits for a
 * second structured-events adapter.
 */
import { accessSync, constants, statSync } from "node:fs";
import { join } from "node:path";
import type { TuiKind } from "@project/shared";
import type { HarnessQueryOptions } from "../../types.js";

export { TUI_KINDS } from "@project/shared";
export type { TuiKind } from "@project/shared";

export interface TuiCapabilities {
  /** Can a later spawn resume a prior session lineage (across sleeps)? */
  resume: boolean;
  /** Does a trusted structured-event source exist (transcript/hooks)?
   *  Gates PtySession's tailer/hook-socket/tool-server/settings machinery,
   *  the submit nudge, and the plan-dialog auto-accept. */
  structuredEvents: boolean;
  /** Is paste-without-submit prompt delivery meaningful for this TUI? */
  prefill: boolean;
  /** Can typing into a parked TUI be detected (passive turns)? */
  passiveTurns: boolean;
  /**
   * Must the first prompt write wait for the raw-TUI readiness probe?
   *
   * opencode SILENTLY DISCARDS stdin for ~2.4s while its TUI paints — a prompt
   * pasted early is lost with no trace, and no submit nudge can recover it
   * (the TEXT is gone, not just the Enter). Its own capability rather than
   * `!structuredEvents`: gaining a trusted event source (the Conveyor events
   * plugin) does not change how the TUI's input box behaves at startup.
   * Claude stays false — probing it would inject keystrokes into every card's
   * first turn, and its startup dialogs are numbered menus where stray input
   * is actively unsafe.
   */
  rawPromptGate: boolean;
}

export interface TuiSpawnInput {
  options: HarnessQueryOptions;
  resume?: string;
  /** Per-process resources allocated by PtySession for structured-events
   *  adapters only; absent otherwise. Claude's ride files/sockets: */
  settingsPath?: string;
  mcpConfigPath?: string;
  hookSocketPath?: string;
  /** opencode's ride its inline config env (OPENCODE_CONFIG_CONTENT): */
  mcpEntries?: Record<string, import("../tool-server.js").McpConfigEntry>;
  eventsSinkPath?: string;
  pluginPath?: string;
  instructionsPath?: string;
}

export interface TuiSpawnSpec {
  file: string;
  args: string[];
  env: Record<string, string>;
}

export interface TuiFingerprintInput {
  model: string;
  permissionMode: "plan" | "bypassPermissions";
  appendSystemPrompt?: string;
  cwd: string;
}

/** Thrown by resolveBinary when the TUI cannot run here — the fail-loud path. */
export class TuiUnavailableError extends Error {
  constructor(
    public readonly tui: TuiKind,
    message: string,
  ) {
    super(message);
    this.name = "TuiUnavailableError";
  }
}

export interface TuiAdapter {
  readonly id: TuiKind;
  readonly capabilities: TuiCapabilities;
  /** Resolve the executable. Throws TuiUnavailableError when it cannot run. */
  resolveBinary(env?: NodeJS.ProcessEnv): string;
  /** Full spawn spec (file + argv + child env) for node-pty. */
  buildSpawn(input: TuiSpawnInput): TuiSpawnSpec;
  /** One-time per-process environment prep before first spawn (credential
   *  files, onboarding seeds). Never on reuse. Must not throw on best-effort
   *  IO failures — log and continue, matching ensureClaudeCredentials. */
  prepareEnvironment(opts: { cwd: string; env?: NodeJS.ProcessEnv }): Promise<void>;
  /** Park/reuse invalidation key. MUST be stable for identical inputs. */
  spawnFingerprint(input: TuiFingerprintInput): string;
  /** Encode prompt text into the stdin bytes for this TUI's input box. */
  encodePromptBytes(text: string): string;
  /** errors[] for a process that died before producing a result. */
  buildExitErrors(exitCode: number, rawOutput: string): string[];
}

function isExecutable(path: string): boolean {
  try {
    if (!statSync(path).isFile()) return false;
    accessSync(path, constants.X_OK);
    return true;
  } catch {
    return false;
  }
}

/**
 * Minimal `which`: locate `binary` on env.PATH (or verify a given path when it
 * contains a slash). Returns null when not found/executable — callers turn
 * that into TuiUnavailableError.
 */
export function findOnPath(binary: string, env: NodeJS.ProcessEnv = process.env): string | null {
  if (binary.includes("/")) {
    return isExecutable(binary) ? binary : null;
  }
  for (const dir of (env.PATH ?? "").split(":")) {
    if (!dir) continue;
    const candidate = join(dir, binary);
    if (isExecutable(candidate)) return candidate;
  }
  return null;
}
